import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, Plus, Wand2 } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { ApiError } from "@/api/client";
import { formatDateTimeInTz, useUserTimeZone } from "@/utils/datetime";
import {
  answerAssistantApi,
  type AnswerAssistantAction,
  type AnswerAssistantRefinementTurn,
  type AnswerAssistantResult,
} from "@/api/answerAssistant";

// LLM & Reports v3.2 §9 — inline AI Answer Assistant modal. One
// component handles both Improve and Expand via the `action` prop;
// the title, microcopy, loading text, and underlying API endpoint
// swap together. The action does NOT affect the button labels —
// every Improve / Expand session ends with the same four buttons:
// Cancel, Refine again, Append to original, Replace original.
//
// Footer button order (left → right) per spec §9:
//   Cancel              — secondary; closes without saving
//   Refine again        — tertiary; sends a new request with the
//                          refinement-history; hidden when at the
//                          MaxRefinementTurns cap or after a 429
//   Append to original  — secondary + Plus icon; calls onAppend
//                          and closes
//   Replace original    — primary (gold) + Check icon; calls
//                          onAccept and closes
//
// Quota and refinement-turn limits surface inline:
//   - 429 disables Refine again with the reset-at-midnight message
//   - history.length >= MAX_REFINEMENT_TURNS hides Refine again

const MAX_REFINEMENT_TURNS = 5;

export interface AnswerAssistantModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Replace the form field's value entirely with the assistant's
   * draft. Called when the user clicks "Replace original".
   */
  onAccept: (newText: string) => void;
  /**
   * Append the assistant's draft to the form field's existing value
   * (joined with two newlines so paragraphs read naturally). Called
   * when the user clicks "Append to original".
   */
  onAppend: (additionalText: string) => void;
  reportSubmissionId: string;
  questionId: string;
  /** The form field's current value; the assistant works against this. */
  originalText: string;
  /** Improve refines clarity / grammar; Expand extends with new detail. */
  action: AnswerAssistantAction;
}

// Per-action copy that DOES vary: title, microcopy, loading text,
// and the heading on the editable-draft section. Button labels are
// intentionally absent — those are constant across both actions.
const ACTION_COPY: Record<AnswerAssistantAction, {
  title: string;
  subhead: string;
  loading: string;
  draftHeading: string;
}> = {
  improve: {
    title: "Improve answer",
    subhead:
      "Phoenix Capital's assistant will refine the answer for clarity, grammar, and tone — without changing the substance.",
    loading: "Improving your answer…",
    draftHeading: "Improved draft",
  },
  expand: {
    title: "Expand answer",
    subhead:
      "Phoenix Capital's assistant will extend the answer with relevant detail — keeping the original intact.",
    loading: "Expanding your answer…",
    draftHeading: "Expanded draft",
  },
};

export function AnswerAssistantModal({
  open,
  onClose,
  onAccept,
  onAppend,
  reportSubmissionId,
  questionId,
  originalText,
  action,
}: AnswerAssistantModalProps) {
  const cfg = ACTION_COPY[action];
  const tz = useUserTimeZone();
  const [draft, setDraft] = useState("");
  const [refinementInstruction, setRefinementInstruction] = useState("");
  const [history, setHistory] = useState<AnswerAssistantRefinementTurn[]>([]);
  const [quotaResetAt, setQuotaResetAt] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [remainingQuota, setRemainingQuota] = useState<number | null>(null);

  const callApi = (instruction?: string) => {
    setErrorMessage(null);
    const body = {
      originalText,
      refinementHistory:
        instruction && draft
          ? [
              ...history,
              {
                userInstruction: instruction,
                assistantOutput: draft,
              },
            ]
          : history.length === 0
            ? null
            : history,
    };
    return action === "improve"
      ? answerAssistantApi.improve(reportSubmissionId, questionId, body)
      : answerAssistantApi.expand(reportSubmissionId, questionId, body);
  };

  const draftMutation = useMutation<AnswerAssistantResult, Error, string | undefined>({
    mutationFn: (instruction) => callApi(instruction),
    onSuccess: (result, instruction) => {
      setDraft(result.draft);
      setRemainingQuota(result.remainingDailyQuota);
      if (instruction) {
        setHistory((cur) => [
          ...cur,
          { userInstruction: instruction, assistantOutput: draft },
        ]);
        setRefinementInstruction("");
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        // Quota exhausted. Disable Refine again; let Replace / Append
        // / Cancel remain available so any previously generated
        // draft is still actionable.
        setQuotaResetAt(nextUtcMidnightLocal(tz));
        setErrorMessage(err.message ?? "Daily limit reached.");
      } else {
        setErrorMessage(err.message ?? "The assistant couldn't reply. Try again.");
      }
    },
  });

  // Fire the first call automatically when the modal opens. Reset
  // state when it closes so a re-open against a different action /
  // question doesn't carry stale draft text.
  useEffect(() => {
    if (open) {
      setDraft("");
      setRefinementInstruction("");
      setHistory([]);
      setQuotaResetAt(null);
      setErrorMessage(null);
      setRemainingQuota(null);
      draftMutation.mutate(undefined);
    }
    // We intentionally trigger only on open — re-firing on every
    // mutation state change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, questionId, reportSubmissionId]);

  const refinementLimitReached = history.length >= MAX_REFINEMENT_TURNS;
  const quotaExhausted = quotaResetAt !== null;
  const refineAvailable = !refinementLimitReached && !quotaExhausted;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={cfg.title}
      description={cfg.subhead}
      size="lg"
      footer={
        // Spec §9 footer — Cancel · Refine again · Append (with +) ·
        // Replace original (primary, with Check). Labels are
        // constant across Improve and Expand; only the icon on
        // Append is the Plus that visually differentiates it from
        // Replace's Check.
        <div className="flex flex-wrap items-center justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {refineAvailable && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => draftMutation.mutate(refinementInstruction.trim())}
              disabled={
                !refinementInstruction.trim() ||
                draftMutation.isPending ||
                !draft
              }
            >
              <Wand2 size={14} aria-hidden /> Refine again
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => onAppend(draft)}
            disabled={!draft || draftMutation.isPending}
          >
            <Plus size={16} aria-hidden /> Append to original
          </Button>
          <Button
            type="button"
            onClick={() => onAccept(draft)}
            disabled={!draft || draftMutation.isPending}
          >
            <Check size={16} aria-hidden /> Replace original
          </Button>
        </div>
      }
    >
      <div className="space-y-space-4">
        <section>
          <h3 className="font-display text-h4 text-onyx mb-space-2">
            Original answer
          </h3>
          <pre className="bg-white-warm rounded-md border border-border-light p-space-3 font-body text-body text-onyx whitespace-pre-wrap">
            {originalText}
          </pre>
        </section>

        <section>
          <h3 className="font-display text-h4 text-onyx mb-space-2">
            {cfg.draftHeading}
          </h3>
          {draftMutation.isPending && history.length === 0 ? (
            <p className="font-body text-body text-gray-mid">{cfg.loading}</p>
          ) : (
            <Textarea
              rows={10}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Assistant draft (editable before accepting)"
              disabled={draftMutation.isPending}
            />
          )}
          {errorMessage && (
            <p
              role="alert"
              className="font-body text-caption text-status-error mt-space-2"
            >
              {errorMessage}
              {quotaExhausted && quotaResetAt && (
                <> The limit resets at midnight UTC ({quotaResetAt} local).</>
              )}
            </p>
          )}
          {remainingQuota !== null && !quotaExhausted && (
            <p className="font-body text-caption text-gray-mid mt-space-1">
              {remainingQuota} of {remainingQuota + (history.length + 1)} refinements
              remaining today.
            </p>
          )}
        </section>

        {refineAvailable && (
          <section>
            <h3 className="font-display text-h4 text-onyx mb-space-2">
              Refinement instruction (optional)
            </h3>
            <p className="font-body text-caption text-gray-mid mb-space-2">
              Tell the assistant how to adjust the draft, then click
              <strong> Refine again</strong> below. Up to{" "}
              {MAX_REFINEMENT_TURNS - history.length} more refinement
              {MAX_REFINEMENT_TURNS - history.length === 1 ? "" : "s"} this
              session.
            </p>
            <Textarea
              rows={2}
              value={refinementInstruction}
              onChange={(e) => setRefinementInstruction(e.target.value)}
              placeholder="e.g. Make it shorter and more direct."
              aria-label="Refinement instruction"
              disabled={draftMutation.isPending}
            />
          </section>
        )}

        {refinementLimitReached && !quotaExhausted && (
          <p className="font-body text-caption text-gray-mid">
            You've reached the {MAX_REFINEMENT_TURNS}-refinement limit for this
            session. Accept or cancel to start fresh.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Local-time string for the next UTC midnight. Used in the quota
 * banner so the user sees when the limit resets in their own clock.
 */
function nextUtcMidnightLocal(tz: string): string {
  const now = new Date();
  const utcMidnight = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
      0,
      0,
      0,
      0,
    ),
  );
  return formatDateTimeInTz(utcMidnight, tz);
}
